// app/screens/professional/FreelanceJobsScreen.js
// JobEng - Comprehensive Freelance Jobs Marketplace
//
// 5 tabs: Browse | Saved | Applied | My Posts | Earnings
// Features: search, multi-filter, sort, bid/proposal modal, save, share,
// AI-matched job recommendations, earnings dashboard, statistics
//
// Dynamic import for UnifiedAPIService with safe fallbacks — works even if the
// service exports change.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Modal,
  SafeAreaView,
  Alert,
  StatusBar,
  Dimensions,
  ActivityIndicator,
  FlatList,
  RefreshControl,
  Platform,
  KeyboardAvoidingView,
  Share,
  Pressable,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// Optional UnifiedAPIService — try named imports, fall back to apiFetch directly
let UnifiedAPI = null;
try {
  UnifiedAPI = require('../../utils/UnifiedAPIService');
} catch (e) {
  // Not installed — we'll use apiFetch directly
}
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');
// Optional icons with safe fallback
const FallbackIcon = ({ size = 24, color = '#666' }) => (
  <View style={{ width: size, height: size, backgroundColor: color, borderRadius: size / 2, opacity: 0.3 }} />
);
let MaterialCommunityIcons = FallbackIcon;
let Ionicons = FallbackIcon;
try {
  const icons = require('@expo/vector-icons');
  if (icons?.MaterialCommunityIcons) MaterialCommunityIcons = icons.MaterialCommunityIcons;
  if (icons?.Ionicons) Ionicons = icons.Ionicons;
} catch {}

let LinearGradient = ({ children, style, ...rest }) => <View style={style} {...rest}>{children}</View>;
try {
  const lg = require('expo-linear-gradient');
  if (lg?.LinearGradient) LinearGradient = lg.LinearGradient;
} catch {}

const { width: SCREEN_W } = Dimensions.get('window');

// ──────────────────────────────────────────────────────────────────────────────
// Theme
// ──────────────────────────────────────────────────────────────────────────────
const COLORS = {
  primary:   '#0e4e96',
  primaryDk: '#0a3d75',
  accent:    '#7C3AED',
  success:   '#10B981',
  warning:   '#F59E0B',
  danger:    '#EF4444',
  info:      '#06B6D4',
  pink:      '#EC4899',
  text:      '#1F2937',
  sub:       '#6B7280',
  light:     '#9CA3AF',
  bg:        '#F3F4F6',
  bgAlt:     '#F9FAFB',
  surface:   '#FFFFFF',
  border:    '#E5E7EB',
};

// ──────────────────────────────────────────────────────────────────────────────
// Categories & filters
// ──────────────────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',         label: 'All',         icon: 'view-grid' },
  { id: 'development', label: 'Development', icon: 'code-tags' },
  { id: 'design',      label: 'Design',      icon: 'palette' },
  { id: 'writing',     label: 'Writing',     icon: 'pencil' },
  { id: 'marketing',   label: 'Marketing',   icon: 'bullhorn' },
  { id: 'video',       label: 'Video',       icon: 'video' },
  { id: 'data',        label: 'Data',        icon: 'database' },
  { id: 'consulting',  label: 'Consulting',  icon: 'briefcase' },
  { id: 'translation', label: 'Translation', icon: 'translate' },
  { id: 'other',       label: 'Other',       icon: 'dots-horizontal' },
];

const BUDGET_RANGES = [
  { id: 'any',    label: 'Any',          min: 0,     max: Infinity },
  { id: 'small',  label: '< £500',       min: 0,     max: 500 },
  { id: 'medium', label: '£500–£2K',     min: 500,   max: 2000 },
  { id: 'large',  label: '£2K–£10K',     min: 2000,  max: 10000 },
  { id: 'xl',     label: '£10K+',        min: 10000, max: Infinity },
];

const DURATIONS = [
  { id: 'any',     label: 'Any' },
  { id: 'oneoff',  label: 'One-off (< 1 week)' },
  { id: 'short',   label: 'Short (1–4 weeks)' },
  { id: 'medium',  label: 'Medium (1–3 months)' },
  { id: 'long',    label: 'Long (3+ months)' },
  { id: 'ongoing', label: 'Ongoing' },
];

const SORTS = [
  { id: 'relevance', label: 'Most Relevant' },
  { id: 'newest',    label: 'Newest First' },
  { id: 'budget_h',  label: 'Highest Budget' },
  { id: 'budget_l',  label: 'Lowest Budget' },
  { id: 'fewest',    label: 'Fewest Bids' },
];

const JOB_STATUSES = ['open', 'in_progress', 'completed', 'cancelled'];
const APPLICATION_STATUSES = {
  submitted:  { label: 'Submitted', color: COLORS.info },
  shortlisted:{ label: 'Shortlisted', color: COLORS.warning },
  interview:  { label: 'Interview', color: COLORS.accent },
  hired:      { label: 'Hired', color: COLORS.success },
  rejected:   { label: 'Not selected', color: COLORS.danger },
  withdrawn:  { label: 'Withdrawn', color: COLORS.sub },
};

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────
const formatCurrency = (v, currency = 'GBP') => new Intl.NumberFormat('en-GB', {
  style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(parseFloat(v) || 0);

const formatRelativeDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24)  return `${hrs}h ago`;
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const sampleSeed = () => ([
  { id: 1, title: 'React Native Developer — Mobile App', description: 'Build a 2-tab mobile app for a fitness startup. Need clean code, good UI, and Expo experience.', category: 'development', budget: 3500, currency: 'GBP', duration: 'short', skills: ['React Native', 'Expo', 'TypeScript'], bidCount: 12, location: 'Remote', clientName: 'FitLife Ltd', clientRating: 4.8, postedAt: new Date(Date.now() - 3 * 3600000).toISOString(), status: 'open', verified: true },
  { id: 2, title: 'Logo + Brand Identity Design', description: 'Need a modern logo and brand guidelines for a tech consulting startup. Looking for a designer who can deliver in 2 weeks.', category: 'design', budget: 850, currency: 'GBP', duration: 'short', skills: ['Branding', 'Adobe Illustrator', 'Typography'], bidCount: 27, location: 'Remote', clientName: 'TechAdvise', clientRating: 4.6, postedAt: new Date(Date.now() - 1 * 86400000).toISOString(), status: 'open', verified: true },
  { id: 3, title: 'Long-form Article Writer (SaaS)', description: 'Looking for an experienced B2B SaaS writer to produce 4 articles per month, 1500–2500 words each.', category: 'writing', budget: 2400, currency: 'GBP', duration: 'ongoing', skills: ['B2B Writing', 'SEO', 'Research'], bidCount: 18, location: 'Remote', clientName: 'CloudOps', clientRating: 4.9, postedAt: new Date(Date.now() - 2 * 86400000).toISOString(), status: 'open', verified: false },
  { id: 4, title: 'Senior Data Analyst — Dashboards', description: 'Build a sales analytics dashboard in Looker Studio with our existing BigQuery setup. Estimated 60 hours.', category: 'data', budget: 5400, currency: 'GBP', duration: 'medium', skills: ['SQL', 'Looker Studio', 'BigQuery'], bidCount: 7, location: 'Remote', clientName: 'Northwind Trading', clientRating: 5.0, postedAt: new Date(Date.now() - 5 * 86400000).toISOString(), status: 'open', verified: true },
  { id: 5, title: 'YouTube Video Editor (Weekly)', description: 'Need a video editor for a finance YouTube channel. Weekly delivery, 10-15 min videos with B-roll, motion graphics, and captions.', category: 'video', budget: 1800, currency: 'GBP', duration: 'ongoing', skills: ['Premiere Pro', 'After Effects', 'Motion Graphics'], bidCount: 34, location: 'Remote', clientName: 'MoneyMatters', clientRating: 4.7, postedAt: new Date(Date.now() - 6 * 3600000).toISOString(), status: 'open', verified: true },
]);

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SCREEN
// ══════════════════════════════════════════════════════════════════════════════
export default function FreelanceJobsScreen({ navigation, route }) {
  // ── Tab + lifecycle ───────────────────────────────────────────────────────
  const [activeTab, setActiveTab]     = useState(route?.params?.tab || 'browse');
  const [loading, setLoading]         = useState(true);
  const [refreshing, setRefreshing]   = useState(false);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [jobs, setJobs]               = useState([]);
  const [savedIds, setSavedIds]       = useState(new Set());
  const [applications, setApplications] = useState([]); // user's submitted bids
  const [myPosts, setMyPosts]         = useState([]);    // jobs user posted as client
  const [earnings, setEarnings]       = useState({ total: 0, pending: 0, paid: 0, jobs: 0, history: [] });
  const [userProfile, setUserProfile] = useState(null);

  // ── Filter/search state ───────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [budgetFilter, setBudgetFilter]   = useState('any');
  const [durationFilter, setDurationFilter] = useState('any');
  const [sortBy, setSortBy]               = useState('relevance');
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  // ── Modal state ───────────────────────────────────────────────────────────
  const [selectedJob, setSelectedJob]     = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBidModal, setShowBidModal]   = useState(false);
  const [bidAmount, setBidAmount]         = useState('');
  const [bidProposal, setBidProposal]     = useState('');
  const [bidDuration, setBidDuration]     = useState('');
  const [submittingBid, setSubmittingBid] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [editingPost, setEditingPost]     = useState(null);

  // ══════════════════════════════════════════════════════════════════════════
  // Init
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => { init(); }, []);

  const init = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadUserProfile(),
        loadJobs(),
        loadSavedJobs(),
        loadApplications(),
        loadMyPosts(),
        loadEarnings(),
      ]);
    } catch (e) {
      console.error('init:', e);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await init();
    setRefreshing(false);
  };

  // ── Loaders (all gracefully fall back) ────────────────────────────────────
  const loadUserProfile = async () => {
    try {
      await ProfileManagementService.bootstrap();
      const r = await ProfileManagementService.getProfile();
      if (r.success && r.data) setUserProfile(r.data);
    } catch {}
  };

  const loadJobs = async () => {
    try {
      // Try UnifiedAPIService first if available
      if (UnifiedAPI?.searchJobsByType) {
        try {
          const r = await UnifiedAPI.searchJobsByType('freelance');
          if (Array.isArray(r) && r.length > 0) { setJobs(r); return; }
          if (Array.isArray(r?.data) && r.data.length > 0) { setJobs(r.data); return; }
        } catch {}
      }
      if (UnifiedAPI?.fetchFreelanceJobs) {
        try {
          const r = await UnifiedAPI.fetchFreelanceJobs();
          if (Array.isArray(r) && r.length > 0) { setJobs(r); return; }
        } catch {}
      }

      // Fall back to direct API
      const r = await apiFetch('/api/data/freelance_jobs?status=open');
      const data = Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : []);
      if (data.length > 0) {
        setJobs(data);
      } else {
        // Last fallback: try cache, otherwise seed with sample for first-run UX
        const cached = await AsyncStorage.getItem('freelance_jobs_cache');
        setJobs(cached ? JSON.parse(cached) : sampleSeed());
      }
    } catch {
      const cached = await AsyncStorage.getItem('freelance_jobs_cache');
      setJobs(cached ? JSON.parse(cached) : sampleSeed());
    }
  };

  const loadSavedJobs = async () => {
    try {
      const raw = await AsyncStorage.getItem('freelance_saved_ids');
      if (raw) setSavedIds(new Set(JSON.parse(raw)));
    } catch {}
  };

  const loadApplications = async () => {
    try {
      const r = await apiFetch('/api/data/freelance_applications');
      const data = Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : []);
      if (data.length > 0) {
        setApplications(data);
      } else {
        const cached = await AsyncStorage.getItem('freelance_applications_cache');
        if (cached) setApplications(JSON.parse(cached));
      }
    } catch {}
  };

  const loadMyPosts = async () => {
    try {
      const r = await apiFetch('/api/data/freelance_jobs?my_posts=1');
      const data = Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : []);
      if (data.length > 0) {
        setMyPosts(data);
      } else {
        const cached = await AsyncStorage.getItem('freelance_myposts_cache');
        if (cached) setMyPosts(JSON.parse(cached));
      }
    } catch {}
  };

  const loadEarnings = async () => {
    try {
      const r = await apiFetch('/api/data/freelance_earnings');
      if (r?.data) {
        setEarnings(r.data);
      } else {
        const cached = await AsyncStorage.getItem('freelance_earnings_cache');
        if (cached) setEarnings(JSON.parse(cached));
      }
    } catch {}
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Filtering + sorting + AI matching
  // ══════════════════════════════════════════════════════════════════════════
  const userSkills = useMemo(() => {
    if (!userProfile?.skills) return [];
    return userProfile.skills.map(s => (typeof s === 'string' ? s : (s.name || s.skill_name || ''))).filter(Boolean);
  }, [userProfile]);

  const matchScore = useCallback((job) => {
    if (!userSkills.length || !job.skills) return 0;
    const jobSkills = (job.skills || []).map(s => String(s).toLowerCase());
    const userSet = userSkills.map(s => String(s).toLowerCase());
    const matches = jobSkills.filter(js => userSet.some(us => us.includes(js) || js.includes(us))).length;
    return Math.round((matches / Math.max(jobSkills.length, 1)) * 100);
  }, [userSkills]);

  const filteredJobs = useMemo(() => {
    let items = [...jobs];

    // Category
    if (categoryFilter !== 'all') items = items.filter(j => j.category === categoryFilter);

    // Budget
    if (budgetFilter !== 'any') {
      const range = BUDGET_RANGES.find(r => r.id === budgetFilter);
      if (range) items = items.filter(j => {
        const b = parseFloat(j.budget) || 0;
        return b >= range.min && b <= range.max;
      });
    }

    // Duration
    if (durationFilter !== 'any') items = items.filter(j => j.duration === durationFilter);

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(j => JSON.stringify(j).toLowerCase().includes(q));
    }

    // Sort
    switch (sortBy) {
      case 'newest':    items.sort((a, b) => new Date(b.postedAt || 0) - new Date(a.postedAt || 0)); break;
      case 'budget_h':  items.sort((a, b) => (parseFloat(b.budget) || 0) - (parseFloat(a.budget) || 0)); break;
      case 'budget_l':  items.sort((a, b) => (parseFloat(a.budget) || 0) - (parseFloat(b.budget) || 0)); break;
      case 'fewest':    items.sort((a, b) => (a.bidCount || 0) - (b.bidCount || 0)); break;
      case 'relevance':
      default:          items.sort((a, b) => matchScore(b) - matchScore(a)); break;
    }
    return items;
  }, [jobs, categoryFilter, budgetFilter, durationFilter, searchQuery, sortBy, matchScore]);

  const savedJobs = useMemo(() => jobs.filter(j => savedIds.has(j.id)), [jobs, savedIds]);

  // ══════════════════════════════════════════════════════════════════════════
  // Actions
  // ══════════════════════════════════════════════════════════════════════════
  const toggleSave = async (job) => {
    const next = new Set(savedIds);
    if (next.has(job.id)) next.delete(job.id);
    else next.add(job.id);
    setSavedIds(next);
    try {
      await AsyncStorage.setItem('freelance_saved_ids', JSON.stringify(Array.from(next)));
      // Best-effort backend sync
      apiFetch(`/api/data/freelance_jobs/${job.id}/save`, { method: 'POST' }).catch(() => {});
    } catch {}
  };

  const openBid = (job) => {
    setSelectedJob(job);
    setBidAmount(String(job.budget || ''));
    setBidProposal('');
    setBidDuration('');
    setShowDetailModal(false);
    setShowBidModal(true);
  };

  const submitBid = async () => {
    if (!bidAmount || !bidProposal.trim()) {
      Alert.alert('Required', 'Bid amount and proposal are required');
      return;
    }
    if (bidProposal.trim().length < 50) {
      Alert.alert('Too short', 'Proposal should be at least 50 characters');
      return;
    }
    setSubmittingBid(true);
    try {
      const payload = {
        job_id: selectedJob.id,
        bid_amount: parseFloat(bidAmount),
        proposal: bidProposal,
        duration_estimate: bidDuration,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
      };
      let saved;
      const r = await apiFetch('/api/data/freelance_applications', {
        method: 'POST', body: JSON.stringify(payload),
      }).catch(() => null);
      if (r?.success) {
        saved = r.data || payload;
      } else {
        saved = { ...payload, id: `local_${Date.now()}`, local_only: true, job: selectedJob };
      }
      const newApps = [saved, ...applications];
      setApplications(newApps);
      await AsyncStorage.setItem('freelance_applications_cache', JSON.stringify(newApps));
      setShowBidModal(false);
      Alert.alert(
        saved.local_only ? 'Saved locally' : 'Bid Submitted',
        `Your proposal for "${selectedJob.title}" was ${saved.local_only ? 'saved on this device — backend sync will retry' : 'submitted'}.`
      );
    } catch (e) {
      Alert.alert('Error', e.message);
    } finally {
      setSubmittingBid(false);
    }
  };

  const withdrawApplication = (app) => {
    Alert.alert('Withdraw?', 'Withdraw your bid? This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Withdraw', style: 'destructive',
        onPress: async () => {
          const updated = applications.map(a => a.id === app.id ? { ...a, status: 'withdrawn' } : a);
          setApplications(updated);
          await AsyncStorage.setItem('freelance_applications_cache', JSON.stringify(updated));
          apiFetch(`/api/data/freelance_applications/${app.id}`, {
            method: 'PATCH', body: JSON.stringify({ status: 'withdrawn' }),
          }).catch(() => {});
        },
      },
    ]);
  };

  const shareJob = async (job) => {
    try {
      await Share.share({
        message: `Check out this freelance opportunity:\n\n${job.title}\nBudget: ${formatCurrency(job.budget, job.currency)}\n\n${job.description?.slice(0, 200)}`,
        title: job.title,
      });
    } catch {}
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Render — Header
  // ══════════════════════════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={[COLORS.primary, COLORS.primaryDk]} style={st.header}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFF" />
        </TouchableOpacity>
        <View style={{ flex: 1, marginLeft: 12 }}>
          <Text style={st.hTitle}>Freelance Jobs</Text>
          <Text style={st.hSub}>
            {jobs.length} jobs · {applications.length} applications
          </Text>
        </View>
        <TouchableOpacity style={st.hBtn} onPress={() => { setEditingPost(null); setShowPostModal(true); }}>
          <MaterialCommunityIcons name="plus" size={22} color="#FFF" />
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );

  // ── Main tabs ──────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'browse',   label: 'Browse',     icon: 'magnify',          count: filteredJobs.length },
    { id: 'saved',    label: 'Saved',      icon: 'bookmark-outline', count: savedJobs.length },
    { id: 'applied',  label: 'Applied',    icon: 'send-outline',     count: applications.length },
    { id: 'posts',    label: 'My Posts',   icon: 'briefcase-edit',   count: myPosts.length },
    { id: 'earnings', label: 'Earnings',   icon: 'cash-multiple',    count: null },
  ];

  const renderTabs = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.tabsRow} contentContainerStyle={{ paddingHorizontal: 12 }}>
      {tabs.map(t => (
        <TouchableOpacity
          key={t.id}
          style={[st.tab, activeTab === t.id && { backgroundColor: `${COLORS.primary}15` }]}
          onPress={() => setActiveTab(t.id)}
        >
          <MaterialCommunityIcons name={t.icon} size={16} color={activeTab === t.id ? COLORS.primary : COLORS.sub} />
          <Text style={[st.tabTxt, activeTab === t.id && { color: COLORS.primary, fontWeight: '700' }]}>
            {t.label}
          </Text>
          {t.count !== null && t.count > 0 && (
            <View style={[st.tabBadge, activeTab === t.id && { backgroundColor: COLORS.primary }]}>
              <Text style={st.tabBadgeTxt}>{t.count}</Text>
            </View>
          )}
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ── Toolbar (search + filter) ──────────────────────────────────────────────
  const activeFilterCount = (categoryFilter !== 'all' ? 1 : 0) + (budgetFilter !== 'any' ? 1 : 0) + (durationFilter !== 'any' ? 1 : 0);

  const renderToolbar = () => (
    <View style={st.toolbar}>
      <View style={st.searchBox}>
        <MaterialCommunityIcons name="magnify" size={18} color={COLORS.light} />
        <TextInput
          style={st.searchInput}
          placeholder="Search jobs, skills, keywords..."
          placeholderTextColor={COLORS.light}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
        {searchQuery ? (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close-circle" size={16} color={COLORS.light} />
          </TouchableOpacity>
        ) : null}
      </View>
      <TouchableOpacity style={st.toolBtn} onPress={() => setShowFilterSheet(true)}>
        <MaterialCommunityIcons name="filter-variant" size={20} color={activeFilterCount > 0 ? COLORS.primary : COLORS.sub} />
        {activeFilterCount > 0 && (
          <View style={st.filterBadge}><Text style={st.filterBadgeTxt}>{activeFilterCount}</Text></View>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderCategoryStrip = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.catStrip} contentContainerStyle={{ paddingHorizontal: 12 }}>
      {CATEGORIES.map(c => (
        <TouchableOpacity
          key={c.id}
          style={[st.catChip, categoryFilter === c.id && { backgroundColor: COLORS.primary }]}
          onPress={() => setCategoryFilter(c.id)}
        >
          <MaterialCommunityIcons name={c.icon} size={14} color={categoryFilter === c.id ? '#FFF' : COLORS.sub} />
          <Text style={[st.catChipTxt, categoryFilter === c.id && { color: '#FFF', fontWeight: '700' }]}>{c.label}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Job card (used in Browse + Saved)
  // ══════════════════════════════════════════════════════════════════════════
  const renderJobCard = (job) => {
    const isSaved = savedIds.has(job.id);
    const match = matchScore(job);
    return (
      <TouchableOpacity
        key={job.id}
        style={st.card}
        onPress={() => { setSelectedJob(job); setShowDetailModal(true); }}
      >
        <View style={st.cardTop}>
          <View style={{ flex: 1, marginRight: 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={st.cardTitle} numberOfLines={2}>{job.title}</Text>
              {job.verified && (
                <MaterialCommunityIcons name="check-decagram" size={14} color={COLORS.success} />
              )}
            </View>
            <Text style={st.cardClient}>
              {job.clientName || 'Client'}
              {job.clientRating ? `  ★ ${job.clientRating}` : ''}
              {job.location ? `  ·  ${job.location}` : ''}
            </Text>
          </View>
          <TouchableOpacity onPress={() => toggleSave(job)} hitSlop={{ top: 10, left: 10, right: 10, bottom: 10 }}>
            <MaterialCommunityIcons name={isSaved ? 'bookmark' : 'bookmark-outline'} size={22} color={isSaved ? COLORS.warning : COLORS.light} />
          </TouchableOpacity>
        </View>

        {job.description ? (
          <Text style={st.cardDesc} numberOfLines={3}>{job.description}</Text>
        ) : null}

        {Array.isArray(job.skills) && job.skills.length > 0 && (
          <View style={st.skillRow}>
            {job.skills.slice(0, 4).map((s, i) => (
              <View key={i} style={st.skillChip}>
                <Text style={st.skillChipTxt}>{s}</Text>
              </View>
            ))}
            {job.skills.length > 4 && <Text style={st.skillMore}>+{job.skills.length - 4}</Text>}
          </View>
        )}

        <View style={st.cardFooter}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Text style={st.budget}>{formatCurrency(job.budget, job.currency)}</Text>
            <Text style={st.metaTxt}>
              <MaterialCommunityIcons name="clock-outline" size={11} color={COLORS.sub} /> {formatRelativeDate(job.postedAt)}
            </Text>
            <Text style={st.metaTxt}>
              <MaterialCommunityIcons name="account-multiple-outline" size={11} color={COLORS.sub} /> {job.bidCount || 0} bids
            </Text>
          </View>
          {match > 0 && (
            <View style={[st.matchPill, { backgroundColor: match > 70 ? `${COLORS.success}20` : match > 40 ? `${COLORS.warning}20` : `${COLORS.sub}20` }]}>
              <MaterialCommunityIcons name="target" size={11} color={match > 70 ? COLORS.success : match > 40 ? COLORS.warning : COLORS.sub} />
              <Text style={[st.matchTxt, { color: match > 70 ? COLORS.success : match > 40 ? COLORS.warning : COLORS.sub }]}>{match}%</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // ── Application card (Applied tab) ─────────────────────────────────────────
  const renderApplicationCard = (app) => {
    const job = app.job || jobs.find(j => j.id === app.job_id) || { title: `Job #${app.job_id}` };
    const status = APPLICATION_STATUSES[app.status] || APPLICATION_STATUSES.submitted;
    return (
      <View key={app.id} style={st.card}>
        <View style={st.cardTop}>
          <View style={{ flex: 1 }}>
            <Text style={st.cardTitle} numberOfLines={1}>{job.title}</Text>
            <Text style={st.cardClient}>Submitted {formatRelativeDate(app.submitted_at)}</Text>
          </View>
          <View style={[st.statusPill, { backgroundColor: `${status.color}20` }]}>
            <Text style={[st.statusTxt, { color: status.color }]}>{status.label}</Text>
          </View>
        </View>
        <Text style={st.cardDesc} numberOfLines={3}>{app.proposal}</Text>
        <View style={st.cardFooter}>
          <Text style={st.budget}>Your bid: {formatCurrency(app.bid_amount, job.currency || 'GBP')}</Text>
          {(app.status === 'submitted' || app.status === 'shortlisted') && (
            <TouchableOpacity onPress={() => withdrawApplication(app)}>
              <Text style={[st.linkTxt, { color: COLORS.danger }]}>Withdraw</Text>
            </TouchableOpacity>
          )}
        </View>
        {app.local_only && (
          <View style={st.localBanner}>
            <MaterialCommunityIcons name="cloud-off-outline" size={12} color={COLORS.warning} />
            <Text style={st.localTxt}>Saved on this device — backend sync pending</Text>
          </View>
        )}
      </View>
    );
  };

  // ── My posts card (jobs the user posted as a client) ──────────────────────
  const renderMyPostCard = (post) => (
    <View key={post.id} style={st.card}>
      <View style={st.cardTop}>
        <View style={{ flex: 1 }}>
          <Text style={st.cardTitle} numberOfLines={1}>{post.title}</Text>
          <Text style={st.cardClient}>Posted {formatRelativeDate(post.postedAt)}</Text>
        </View>
        <View style={[st.statusPill, { backgroundColor: `${COLORS.success}20` }]}>
          <Text style={[st.statusTxt, { color: COLORS.success }]}>{post.status || 'open'}</Text>
        </View>
      </View>
      <View style={st.cardFooter}>
        <Text style={st.budget}>{formatCurrency(post.budget, post.currency)}</Text>
        <Text style={st.metaTxt}>{post.bidCount || 0} proposals received</Text>
        <TouchableOpacity onPress={() => { setEditingPost(post); setShowPostModal(true); }}>
          <Text style={st.linkTxt}>Edit</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  // ── Empty states ───────────────────────────────────────────────────────────
  const renderEmpty = (config) => (
    <View style={st.empty}>
      <MaterialCommunityIcons name={config.icon} size={64} color={COLORS.border} />
      <Text style={st.emptyTitle}>{config.title}</Text>
      <Text style={st.emptySub}>{config.sub}</Text>
      {config.action && (
        <TouchableOpacity style={[st.emptyBtn, { backgroundColor: COLORS.primary }]} onPress={config.action.onPress}>
          <Text style={st.emptyBtnTxt}>{config.action.label}</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  // ── Earnings dashboard ─────────────────────────────────────────────────────
  const renderEarnings = () => (
    <View style={{ padding: 16 }}>
      <LinearGradient colors={[COLORS.success, '#059669']} style={st.earningsCard}>
        <Text style={st.earningsLbl}>Total Earnings</Text>
        <Text style={st.earningsNum}>{formatCurrency(earnings.total)}</Text>
        <View style={st.earningsSplit}>
          <View>
            <Text style={st.earningsSubLbl}>Paid Out</Text>
            <Text style={st.earningsSubNum}>{formatCurrency(earnings.paid)}</Text>
          </View>
          <View>
            <Text style={st.earningsSubLbl}>Pending</Text>
            <Text style={st.earningsSubNum}>{formatCurrency(earnings.pending)}</Text>
          </View>
          <View>
            <Text style={st.earningsSubLbl}>Completed Jobs</Text>
            <Text style={st.earningsSubNum}>{earnings.jobs || 0}</Text>
          </View>
        </View>
      </LinearGradient>

      <Text style={st.sectionTitle}>Earnings History</Text>
      {Array.isArray(earnings.history) && earnings.history.length > 0 ? (
        earnings.history.map((h, i) => (
          <View key={i} style={st.historyRow}>
            <View style={{ flex: 1 }}>
              <Text style={st.historyTitle}>{h.title || h.description}</Text>
              <Text style={st.historyDate}>{formatRelativeDate(h.date || h.paid_at)}</Text>
            </View>
            <Text style={[st.historyAmount, { color: h.status === 'pending' ? COLORS.warning : COLORS.success }]}>
              {formatCurrency(h.amount)}
            </Text>
          </View>
        ))
      ) : (
        <View style={[st.empty, { padding: 24 }]}>
          <MaterialCommunityIcons name="cash-clock" size={40} color={COLORS.border} />
          <Text style={st.emptyTitle}>No earnings history yet</Text>
          <Text style={st.emptySub}>Completed jobs and payouts will appear here</Text>
        </View>
      )}
    </View>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Modals
  // ══════════════════════════════════════════════════════════════════════════
  const renderDetailModal = () => (
    <Modal visible={showDetailModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowDetailModal(false)}>
      <SafeAreaView style={st.modal}>
        <View style={st.modalH}>
          <TouchableOpacity onPress={() => setShowDetailModal(false)}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
          <Text style={st.modalTitle} numberOfLines={1}>{selectedJob?.title || 'Job Details'}</Text>
          <TouchableOpacity onPress={() => selectedJob && shareJob(selectedJob)}>
            <MaterialCommunityIcons name="share-variant" size={22} color={COLORS.primary} />
          </TouchableOpacity>
        </View>

        {selectedJob && (
          <ScrollView style={{ flex: 1, padding: 16 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Text style={st.detailTitle}>{selectedJob.title}</Text>
              {selectedJob.verified && <MaterialCommunityIcons name="check-decagram" size={18} color={COLORS.success} />}
            </View>

            <View style={st.detailRow}>
              <Text style={st.detailClient}>{selectedJob.clientName || 'Client'}</Text>
              {selectedJob.clientRating && <Text style={st.metaTxt}> ★ {selectedJob.clientRating}</Text>}
            </View>

            <View style={st.detailStats}>
              <View style={st.statBox}>
                <Text style={st.statNum}>{formatCurrency(selectedJob.budget, selectedJob.currency)}</Text>
                <Text style={st.statLbl}>Budget</Text>
              </View>
              <View style={st.statBox}>
                <Text style={st.statNum}>{selectedJob.bidCount || 0}</Text>
                <Text style={st.statLbl}>Bids</Text>
              </View>
              <View style={st.statBox}>
                <Text style={st.statNum}>{matchScore(selectedJob)}%</Text>
                <Text style={st.statLbl}>Match</Text>
              </View>
            </View>

            <Text style={st.sectionTitle}>Description</Text>
            <Text style={st.detailBody}>{selectedJob.description}</Text>

            {Array.isArray(selectedJob.skills) && selectedJob.skills.length > 0 && (
              <>
                <Text style={st.sectionTitle}>Skills Required</Text>
                <View style={st.skillRow}>
                  {selectedJob.skills.map((s, i) => {
                    const youHave = userSkills.some(us => String(us).toLowerCase().includes(String(s).toLowerCase()) || String(s).toLowerCase().includes(String(us).toLowerCase()));
                    return (
                      <View key={i} style={[st.skillChip, youHave && { backgroundColor: `${COLORS.success}15`, borderColor: COLORS.success }]}>
                        {youHave && <MaterialCommunityIcons name="check" size={11} color={COLORS.success} />}
                        <Text style={[st.skillChipTxt, youHave && { color: COLORS.success, fontWeight: '600' }]}>{s}</Text>
                      </View>
                    );
                  })}
                </View>
              </>
            )}

            <Text style={st.sectionTitle}>Details</Text>
            <View style={st.detailMeta}>
              <DetailLine icon="map-marker-outline" label="Location" value={selectedJob.location || 'Remote'} />
              <DetailLine icon="clock-outline" label="Duration" value={DURATIONS.find(d => d.id === selectedJob.duration)?.label || 'Not specified'} />
              <DetailLine icon="calendar-outline" label="Posted" value={formatRelativeDate(selectedJob.postedAt)} />
              <DetailLine icon="folder-outline" label="Category" value={CATEGORIES.find(c => c.id === selectedJob.category)?.label || 'Other'} />
            </View>

            <View style={{ flexDirection: 'row', gap: 10, marginTop: 24, marginBottom: 30 }}>
              <TouchableOpacity style={[st.bigBtn, { backgroundColor: COLORS.bgAlt, flex: 1 }]} onPress={() => toggleSave(selectedJob)}>
                <MaterialCommunityIcons name={savedIds.has(selectedJob.id) ? 'bookmark' : 'bookmark-outline'} size={18} color={COLORS.text} />
                <Text style={[st.bigBtnTxt, { color: COLORS.text }]}>{savedIds.has(selectedJob.id) ? 'Saved' : 'Save'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.bigBtn, { backgroundColor: COLORS.primary, flex: 2 }]} onPress={() => openBid(selectedJob)}>
                <MaterialCommunityIcons name="send" size={18} color="#FFF" />
                <Text style={st.bigBtnTxt}>Submit Proposal</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        )}
      </SafeAreaView>
    </Modal>
  );

  // ── Bid modal ──────────────────────────────────────────────────────────────
  const renderBidModal = () => (
    <Modal visible={showBidModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowBidModal(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={() => setShowBidModal(false)}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={st.modalTitle}>Submit Proposal</Text>
            <TouchableOpacity onPress={submitBid} disabled={submittingBid}>
              {submittingBid ? <ActivityIndicator color={COLORS.primary} /> : <Text style={[st.saveBtn, { color: COLORS.primary }]}>Send</Text>}
            </TouchableOpacity>
          </View>

          {selectedJob && (
            <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
              <View style={st.bidJobBanner}>
                <Text style={st.bidJobTitle}>{selectedJob.title}</Text>
                <Text style={st.bidJobBudget}>Client budget: {formatCurrency(selectedJob.budget, selectedJob.currency)}</Text>
              </View>

              <Text style={st.label}>Your Bid Amount ({selectedJob.currency || 'GBP'})</Text>
              <TextInput
                style={st.input}
                value={bidAmount}
                onChangeText={setBidAmount}
                placeholder="0"
                placeholderTextColor={COLORS.light}
                keyboardType="numeric"
              />

              <Text style={[st.label, { marginTop: 14 }]}>Duration Estimate</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {['1 week', '2 weeks', '1 month', '2 months', '3+ months'].map(d => (
                  <TouchableOpacity
                    key={d}
                    style={[st.chip, bidDuration === d && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]}
                    onPress={() => setBidDuration(d)}
                  >
                    <Text style={[st.chipTxt, bidDuration === d && { color: '#FFF', fontWeight: '700' }]}>{d}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>

              <Text style={[st.label, { marginTop: 14 }]}>Cover Letter / Proposal</Text>
              <TextInput
                style={[st.input, { minHeight: 200, textAlignVertical: 'top' }]}
                value={bidProposal}
                onChangeText={setBidProposal}
                placeholder={`Hi,\n\nI'm interested in your ${selectedJob.title} project because...\n\nMy relevant experience includes...\n\nI can deliver by...\n\nLooking forward to hearing from you.`}
                placeholderTextColor={COLORS.light}
                multiline
              />
              <Text style={st.helper}>{bidProposal.length} characters · minimum 50</Text>

              <View style={{ height: 32 }} />
            </ScrollView>
          )}
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ── Filter sheet ──────────────────────────────────────────────────────────
  const renderFilterSheet = () => (
    <Modal visible={showFilterSheet} animationType="fade" transparent onRequestClose={() => setShowFilterSheet(false)}>
      <Pressable style={st.backdrop} onPress={() => setShowFilterSheet(false)}>
        <Pressable style={st.sheet}>
          <Text style={st.sheetTitle}>Filter & Sort</Text>

          <Text style={[st.label, { marginTop: 14 }]}>Budget</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {BUDGET_RANGES.map(r => (
              <TouchableOpacity
                key={r.id}
                style={[st.chip, budgetFilter === r.id && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]}
                onPress={() => setBudgetFilter(r.id)}
              >
                <Text style={[st.chipTxt, budgetFilter === r.id && { color: '#FFF', fontWeight: '700' }]}>{r.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[st.label, { marginTop: 14 }]}>Duration</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {DURATIONS.map(d => (
              <TouchableOpacity
                key={d.id}
                style={[st.chip, durationFilter === d.id && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]}
                onPress={() => setDurationFilter(d.id)}
              >
                <Text style={[st.chipTxt, durationFilter === d.id && { color: '#FFF', fontWeight: '700' }]}>{d.label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>

          <Text style={[st.label, { marginTop: 14 }]}>Sort By</Text>
          {SORTS.map(s => (
            <TouchableOpacity key={s.id} style={[st.sortRow, sortBy === s.id && { backgroundColor: `${COLORS.primary}10` }]} onPress={() => setSortBy(s.id)}>
              <MaterialCommunityIcons name={sortBy === s.id ? 'radiobox-marked' : 'radiobox-blank'} size={20} color={sortBy === s.id ? COLORS.primary : COLORS.sub} />
              <Text style={[st.sortRowTxt, sortBy === s.id && { color: COLORS.primary, fontWeight: '700' }]}>{s.label}</Text>
            </TouchableOpacity>
          ))}

          <View style={{ flexDirection: 'row', gap: 10, marginTop: 16 }}>
            <TouchableOpacity style={[st.applyBtn, { flex: 1, backgroundColor: COLORS.bgAlt }]} onPress={() => { setCategoryFilter('all'); setBudgetFilter('any'); setDurationFilter('any'); setSortBy('relevance'); }}>
              <Text style={{ color: COLORS.text, fontWeight: '700' }}>Reset</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.applyBtn, { flex: 2, backgroundColor: COLORS.primary }]} onPress={() => setShowFilterSheet(false)}>
              <Text style={{ color: '#FFF', fontWeight: '700' }}>Apply Filters</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );

  // ── Post job modal (user as client) ────────────────────────────────────────
  const [postForm, setPostForm] = useState({ title: '', description: '', category: 'development', budget: '', duration: 'short', skills: '' });
  useEffect(() => {
    if (editingPost) setPostForm({ ...editingPost, skills: Array.isArray(editingPost.skills) ? editingPost.skills.join(', ') : (editingPost.skills || '') });
    else setPostForm({ title: '', description: '', category: 'development', budget: '', duration: 'short', skills: '' });
  }, [editingPost, showPostModal]);

  const submitPost = async () => {
    if (!postForm.title.trim() || !postForm.description.trim()) {
      Alert.alert('Required', 'Title and description are required');
      return;
    }
    const payload = {
      ...postForm,
      skills: postForm.skills.split(',').map(s => s.trim()).filter(Boolean),
      budget: parseFloat(postForm.budget) || 0,
      currency: 'GBP',
      postedAt: editingPost?.postedAt || new Date().toISOString(),
      status: 'open',
      bidCount: editingPost?.bidCount || 0,
    };
    let saved;
    try {
      const r = await apiFetch(
        editingPost?.id ? `/api/data/freelance_jobs/${editingPost.id}` : '/api/data/freelance_jobs',
        { method: editingPost?.id ? 'PUT' : 'POST', body: JSON.stringify(payload) }
      ).catch(() => null);
      saved = r?.success ? r.data : { ...payload, id: editingPost?.id || `local_${Date.now()}`, local_only: !r?.success };
    } catch {
      saved = { ...payload, id: editingPost?.id || `local_${Date.now()}`, local_only: true };
    }
    if (editingPost?.id) setMyPosts(myPosts.map(p => p.id === editingPost.id ? saved : p));
    else setMyPosts([saved, ...myPosts]);
    await AsyncStorage.setItem('freelance_myposts_cache', JSON.stringify(editingPost?.id ? myPosts.map(p => p.id === editingPost.id ? saved : p) : [saved, ...myPosts]));
    setShowPostModal(false);
    setEditingPost(null);
    Alert.alert(saved.local_only ? 'Saved locally' : 'Posted', `"${saved.title}" is ${saved.local_only ? 'saved on this device' : 'live'}`);
  };

  const renderPostModal = () => (
    <Modal visible={showPostModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setShowPostModal(false)}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <SafeAreaView style={st.modal}>
          <View style={st.modalH}>
            <TouchableOpacity onPress={() => { setShowPostModal(false); setEditingPost(null); }}><MaterialCommunityIcons name="close" size={26} color={COLORS.text} /></TouchableOpacity>
            <Text style={st.modalTitle}>{editingPost ? 'Edit Post' : 'Post a Job'}</Text>
            <TouchableOpacity onPress={submitPost}>
              <Text style={[st.saveBtn, { color: COLORS.primary }]}>Post</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={{ flex: 1, padding: 16 }} keyboardShouldPersistTaps="handled">
            <Text style={st.label}>Job Title</Text>
            <TextInput style={st.input} value={postForm.title} onChangeText={(v) => setPostForm({ ...postForm, title: v })} placeholder="e.g. Build a React Native fitness app" placeholderTextColor={COLORS.light} />

            <Text style={[st.label, { marginTop: 14 }]}>Description</Text>
            <TextInput style={[st.input, { minHeight: 160, textAlignVertical: 'top' }]} value={postForm.description} onChangeText={(v) => setPostForm({ ...postForm, description: v })} placeholder="Describe the project, deliverables, expectations..." placeholderTextColor={COLORS.light} multiline />

            <Text style={[st.label, { marginTop: 14 }]}>Category</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {CATEGORIES.filter(c => c.id !== 'all').map(c => (
                <TouchableOpacity key={c.id} style={[st.chip, postForm.category === c.id && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]} onPress={() => setPostForm({ ...postForm, category: c.id })}>
                  <Text style={[st.chipTxt, postForm.category === c.id && { color: '#FFF', fontWeight: '700' }]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>

            <View style={{ flexDirection: 'row', marginTop: 14 }}>
              <View style={{ flex: 1, marginRight: 6 }}>
                <Text style={st.label}>Budget (GBP)</Text>
                <TextInput style={st.input} value={postForm.budget} onChangeText={(v) => setPostForm({ ...postForm, budget: v })} placeholder="0" placeholderTextColor={COLORS.light} keyboardType="numeric" />
              </View>
              <View style={{ flex: 1, marginLeft: 6 }}>
                <Text style={st.label}>Duration</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                  {DURATIONS.filter(d => d.id !== 'any').map(d => (
                    <TouchableOpacity key={d.id} style={[st.chip, postForm.duration === d.id && { backgroundColor: COLORS.primary, borderColor: COLORS.primary }]} onPress={() => setPostForm({ ...postForm, duration: d.id })}>
                      <Text style={[st.chipTxt, postForm.duration === d.id && { color: '#FFF', fontWeight: '700' }]}>{d.label.split(' ')[0]}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>

            <Text style={[st.label, { marginTop: 14 }]}>Skills (comma-separated)</Text>
            <TextInput style={st.input} value={postForm.skills} onChangeText={(v) => setPostForm({ ...postForm, skills: v })} placeholder="React Native, TypeScript, Expo" placeholderTextColor={COLORS.light} />

            <View style={{ height: 32 }} />
          </ScrollView>
        </SafeAreaView>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Final assembly
  // ══════════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <SafeAreaView style={st.loading}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={st.loadingTxt}>Loading freelance jobs...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primary} />
      {renderHeader()}
      {renderTabs()}

      {activeTab === 'browse' && (
        <>
          {renderToolbar()}
          {renderCategoryStrip()}
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingVertical: 8 }}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[COLORS.primary]} />}
          >
            {filteredJobs.length === 0
              ? renderEmpty({ icon: 'briefcase-search-outline', title: 'No jobs match', sub: 'Try adjusting your filters or search', action: { label: 'Clear Filters', onPress: () => { setCategoryFilter('all'); setBudgetFilter('any'); setDurationFilter('any'); setSearchQuery(''); } } })
              : filteredJobs.map(renderJobCard)}
            <View style={{ height: 80 }} />
          </ScrollView>
        </>
      )}

      {activeTab === 'saved' && (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
          {savedJobs.length === 0
            ? renderEmpty({ icon: 'bookmark-outline', title: 'No saved jobs yet', sub: 'Tap the bookmark icon on any job to save it' })
            : savedJobs.map(renderJobCard)}
        </ScrollView>
      )}

      {activeTab === 'applied' && (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
          {applications.length === 0
            ? renderEmpty({ icon: 'send-outline', title: 'No applications yet', sub: 'Submit a proposal on a job to get started', action: { label: 'Browse Jobs', onPress: () => setActiveTab('browse') } })
            : applications.map(renderApplicationCard)}
        </ScrollView>
      )}

      {activeTab === 'posts' && (
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8 }}>
          {myPosts.length === 0
            ? renderEmpty({ icon: 'briefcase-edit-outline', title: 'No posts yet', sub: 'Post a job to find talent', action: { label: 'Post a Job', onPress: () => { setEditingPost(null); setShowPostModal(true); } } })
            : myPosts.map(renderMyPostCard)}
        </ScrollView>
      )}

      {activeTab === 'earnings' && (
        <ScrollView style={{ flex: 1 }}>
          {renderEarnings()}
        </ScrollView>
      )}

      {renderDetailModal()}
      {renderBidModal()}
      {renderFilterSheet()}
      {renderPostModal()}
    </SafeAreaView>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reusable bits
// ──────────────────────────────────────────────────────────────────────────────
const DetailLine = ({ icon, label, value }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, gap: 10 }}>
    <MaterialCommunityIcons name={icon} size={16} color={COLORS.sub} />
    <Text style={{ flex: 1, fontSize: 13, color: COLORS.sub }}>{label}</Text>
    <Text style={{ fontSize: 13, color: COLORS.text, fontWeight: '600' }}>{value}</Text>
  </View>
);

// ──────────────────────────────────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────────────────────────────────
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.bg },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg },
  loadingTxt: { fontSize: 14, color: COLORS.sub, marginTop: 12 },

  // Header
  header: { paddingTop: 8, paddingBottom: 16, paddingHorizontal: 16 },
  hBtn: { width: 40, height: 40, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.12)', justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 20, fontWeight: '700', color: '#FFF' },
  hSub: { fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 2 },

  // Tabs
  tabsRow: { flexGrow: 0, flexShrink: 0, backgroundColor: COLORS.surface, borderBottomWidth: 1, borderBottomColor: COLORS.border, paddingVertical: 8 },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8, marginRight: 6, borderRadius: 16, gap: 6 },
  tabTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },
  tabBadge: { paddingHorizontal: 6, paddingVertical: 1, borderRadius: 10, backgroundColor: COLORS.border, marginLeft: 4 },
  tabBadgeTxt: { fontSize: 10, color: '#FFF', fontWeight: '700' },

  // Toolbar
  toolbar: { flexDirection: 'row', alignItems: 'center', padding: 12, gap: 8, backgroundColor: COLORS.surface },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.bg, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 8, gap: 6 },
  searchInput: { flex: 1, fontSize: 14, color: COLORS.text },
  toolBtn: { width: 40, height: 36, borderRadius: 10, backgroundColor: COLORS.bg, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  filterBadge: { position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: 8, backgroundColor: COLORS.danger, justifyContent: 'center', alignItems: 'center' },
  filterBadgeTxt: { color: '#FFF', fontSize: 9, fontWeight: '800' },

  // Category strip
  catStrip: { flexGrow: 0, flexShrink: 0, backgroundColor: COLORS.surface, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  catChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14, backgroundColor: COLORS.bgAlt, marginRight: 6, gap: 4 },
  catChipTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },

  // Card
  card: { backgroundColor: COLORS.surface, marginHorizontal: 12, marginVertical: 4, padding: 14, borderRadius: 12, borderWidth: 1, borderColor: COLORS.border },
  cardTop: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  cardTitle: { fontSize: 15, fontWeight: '700', color: COLORS.text },
  cardClient: { fontSize: 12, color: COLORS.sub, marginTop: 2 },
  cardDesc: { fontSize: 13, color: COLORS.sub, lineHeight: 18, marginBottom: 10 },
  skillRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 8 },
  skillChip: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12, backgroundColor: COLORS.bgAlt, borderWidth: 1, borderColor: COLORS.border, gap: 4 },
  skillChipTxt: { fontSize: 11, color: COLORS.sub, fontWeight: '600' },
  skillMore: { fontSize: 11, color: COLORS.sub, fontStyle: 'italic', alignSelf: 'center' },
  cardFooter: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4, paddingTop: 10, borderTopWidth: 1, borderTopColor: COLORS.bgAlt },
  budget: { fontSize: 15, fontWeight: '700', color: COLORS.success },
  metaTxt: { fontSize: 11, color: COLORS.sub },
  matchPill: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 6, paddingVertical: 3, borderRadius: 10, gap: 3 },
  matchTxt: { fontSize: 10, fontWeight: '700' },
  statusPill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10 },
  statusTxt: { fontSize: 10, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  linkTxt: { fontSize: 12, fontWeight: '700', color: COLORS.primary },
  localBanner: { flexDirection: 'row', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: COLORS.bgAlt, gap: 6 },
  localTxt: { fontSize: 11, color: COLORS.warning },

  // Empty
  empty: { alignItems: 'center', padding: 40, marginTop: 20 },
  emptyTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, marginTop: 12 },
  emptySub: { fontSize: 13, color: COLORS.sub, marginTop: 4, textAlign: 'center' },
  emptyBtn: { paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10, marginTop: 16 },
  emptyBtnTxt: { color: '#FFF', fontWeight: '700' },

  // Detail modal
  modal: { flex: 1, backgroundColor: COLORS.surface },
  modalH: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: COLORS.border },
  modalTitle: { fontSize: 17, fontWeight: '700', color: COLORS.text, flex: 1, marginHorizontal: 12 },
  saveBtn: { fontSize: 16, fontWeight: '700' },
  detailTitle: { fontSize: 18, fontWeight: '700', color: COLORS.text },
  detailRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  detailClient: { fontSize: 14, color: COLORS.sub },
  detailStats: { flexDirection: 'row', backgroundColor: COLORS.bgAlt, borderRadius: 12, padding: 14, gap: 8, marginBottom: 16 },
  statBox: { flex: 1, alignItems: 'center' },
  statNum: { fontSize: 16, fontWeight: '700', color: COLORS.text },
  statLbl: { fontSize: 11, color: COLORS.sub, marginTop: 2 },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text, marginTop: 12, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  detailBody: { fontSize: 14, color: COLORS.text, lineHeight: 21 },
  detailMeta: { backgroundColor: COLORS.bgAlt, borderRadius: 10, padding: 12 },
  bigBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 14, borderRadius: 12, gap: 6 },
  bigBtnTxt: { color: '#FFF', fontWeight: '700', fontSize: 14 },

  // Bid modal
  bidJobBanner: { backgroundColor: `${COLORS.primary}10`, padding: 12, borderRadius: 10, marginBottom: 16 },
  bidJobTitle: { fontSize: 14, fontWeight: '700', color: COLORS.text },
  bidJobBudget: { fontSize: 12, color: COLORS.sub, marginTop: 4 },
  label: { fontSize: 13, fontWeight: '700', color: COLORS.text, marginBottom: 6 },
  helper: { fontSize: 11, color: COLORS.sub, marginTop: 4 },
  input: { backgroundColor: COLORS.bgAlt, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: COLORS.text, borderWidth: 1, borderColor: COLORS.border },
  chip: { paddingHorizontal: 12, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.bgAlt, marginRight: 6 },
  chipTxt: { fontSize: 12, color: COLORS.sub, fontWeight: '500' },

  // Filter sheet
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: COLORS.surface, padding: 16, borderTopLeftRadius: 22, borderTopRightRadius: 22, paddingBottom: 32, maxHeight: '85%' },
  sheetTitle: { fontSize: 16, fontWeight: '700', color: COLORS.text },
  sortRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 12, paddingHorizontal: 8, borderRadius: 8, gap: 10, marginTop: 2 },
  sortRowTxt: { fontSize: 14, color: COLORS.text },
  applyBtn: { padding: 14, borderRadius: 10, alignItems: 'center' },

  // Earnings
  earningsCard: { padding: 20, borderRadius: 16, marginBottom: 8 },
  earningsLbl: { fontSize: 12, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 0.5 },
  earningsNum: { fontSize: 32, fontWeight: '800', color: '#FFF', marginTop: 4 },
  earningsSplit: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 16, paddingTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.2)' },
  earningsSubLbl: { fontSize: 11, color: 'rgba(255,255,255,0.8)' },
  earningsSubNum: { fontSize: 16, color: '#FFF', fontWeight: '700', marginTop: 2 },
  historyRow: { flexDirection: 'row', alignItems: 'center', padding: 12, backgroundColor: COLORS.surface, borderRadius: 10, marginBottom: 6, borderWidth: 1, borderColor: COLORS.border },
  historyTitle: { fontSize: 14, color: COLORS.text, fontWeight: '600' },
  historyDate: { fontSize: 11, color: COLORS.sub, marginTop: 2 },
  historyAmount: { fontSize: 15, fontWeight: '700' },
});
